import type { Environment, InternalEnvironment } from '../types';

/**
 * Resolved env used internally to pick the CDN origin and forward to
 * `@zerohash-sdk/*-react` components. Combines the public {@link Environment}
 * surface (`'cert' | 'prod'`) with internal-only QA targets
 * ({@link InternalEnvironment} — `'local' | 'dev'`).
 */
export type NewSdkEnv = Environment | InternalEnvironment;

// Not exported. Partners shouldn't use gating; it's for internal e2e only.
type GatingEnv = 'gating';
type AnyEnv = NewSdkEnv | GatingEnv;

/**
 * Map of hostnames that identify each environment. Hostname matching
 * (rather than substring `includes`) prevents misclassification when a
 * partner's `zeroHashAppsURL` happens to contain a keyword like "dev"
 * or "cert" in an unrelated segment (e.g. `certification-partner.com`).
 */
const HOSTNAMES_BY_ENV: Record<AnyEnv, readonly string[]> = {
  local: ['localhost', '127.0.0.1'],
  gating: [
    'onboarding.gating.0hash.com',
    'web-sdk.gating.0hash.com',
  ],
  cert: [
    'web-sdk.cert.0hash.com',
    'web-sdk.sandbox.connect.xyz',
    'onboarding.cert.zerohash.com',
    'web-sdk.cert.zerohash.com',
    'onboarding.cert.zerohash.eu',
    'web-sdk.cert.zerohash.eu',
    'onboarding.sandbox.connect.xyz',
  ],
  dev: [
    'web-sdk.dev.0hash.com',
    'onboarding.dev.0hash.com',
    'onboarding.dev.zerohash.com',
    'web-sdk.dev.zerohash.com',
    'connect-sdk.dev.0hash.com',
  ],
  prod: [], // fallback
};

/**
 * CDN origin for the new `@zerohash-sdk/*` iframes per environment.
 * `messageRouter` trusts this origin and uses it for set-once pinning.
 */
const NEW_SDK_ORIGIN_BY_ENV: Record<AnyEnv, string> = {
  local: 'http://localhost:5173',
  dev: 'https://sdk-cdn.dev.0hash.com',
  gating: 'https://sdk-cdn.gating.0hash.com',
  cert: 'https://sdk-cdn.cert.zerohash.com',
  prod: 'https://sdk-cdn.zerohash.com',
};

/** CDN origin for env; `??` is a JS runtime guard, dead code from TS's perspective. */
export function getNewSdkCdnOrigin(env: AnyEnv): string {
  return NEW_SDK_ORIGIN_BY_ENV[env] ?? NEW_SDK_ORIGIN_BY_ENV.prod;
}

/**
 * Origins the new `@zerohash-sdk/*` iframes may post from; `messageRouter`
 * trusts any of these. Drop the connect.xyz entries once the migration is done.
 */
export const TRUSTED_NEW_SDK_ORIGINS: ReadonlySet<string> = new Set([
  // new CDN (US)
  'https://sdk-cdn.zerohash.com',
  'https://sdk-cdn.cert.zerohash.com',
  'https://sdk-cdn.dev.0hash.com',
  // gating (internal e2e only, not a public CDN target)
  'https://sdk-cdn.gating.0hash.com',
  // new CDN (EU)
  'https://sdk-cdn.zerohash.eu',
  'https://sdk-cdn.cert.zerohash.eu',
  // legacy connect.xyz (during migration)
  'https://sdk.connect.xyz',
  'https://sdk.sandbox.connect.xyz',
  'https://connect-sdk.dev.0hash.com',
  // local dev
  'http://localhost:5173',
]);

/**
 * Tracks URLs that have already triggered the unrecognized-hostname warning
 * so a per-render call site (e.g. AppContainer) doesn't spam the console.
 */
const warnedFallbackUrls = new Set<string>();

/**
 * Derive the environment from the host app's `zeroHashAppsURL`. Returns
 * `'prod'` for any hostname not in the known lists, which is the safest
 * default for a production-hardcoded CDN. When the fallback path is taken
 * we log a one-time `console.warn` so integrators on a host outside the
 * allowlist can spot the misconfiguration instead of silently being
 * pointed at the production CDN.
 */
export function getNewSdkEnv(url: string): AnyEnv {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    warnFallback(url);
    return 'prod';
  }

  for (const env of ['local', 'gating', 'cert', 'dev'] as const) {
    if (HOSTNAMES_BY_ENV[env].includes(hostname)) {
      return env;
    }
  }
  warnFallback(url);
  return 'prod';
}

function warnFallback(url: string): void {
  if (warnedFallbackUrls.has(url)) return;
  warnedFallbackUrls.add(url);
  console.warn(
    `[zh-web-sdk] Could not infer environment from "${url}"; defaulting to "prod". ` +
      `Pass the explicit \`env\` option to ZeroHashSDK to silence this warning.`,
  );
}

/**
 * Test-only: clear the warn-once cache so suites can assert the warning
 * fires per URL without leaking state across tests.
 */
export function _resetWarnedFallbackUrls(): void {
  warnedFallbackUrls.clear();
}

/**
 * Auth-component env literal. `@connect-xyz/auth-react` only accepts
 * `'sandbox' | 'production'` (no `dev` or `local`), so non-prod SDK envs
 * collapse to `sandbox` — Auth's sandbox API serves cert/dev/local data.
 */
export type AuthEnv = 'sandbox' | 'production';

/**
 * Map a resolved env to the Auth component's env literal.
 */
const ANY_ENV_TO_AUTH_ENV: Record<AnyEnv, AuthEnv> = {
  local: 'sandbox',
  dev: 'sandbox',
  gating: 'sandbox',
  cert: 'sandbox',
  prod: 'production',
};

export function toAuthEnv(env: AnyEnv): AuthEnv {
  return ANY_ENV_TO_AUTH_ENV[env] ?? 'production';
}
